/**
 * `/settings` — the operator's own preferences for Brand Center, reached from the avatar
 * menu (SASBND-0009 U2). Today that is exactly one setting: **Appearance**, the
 * light / dark / match-system control that used to sit in the top bar as `ThemeModeToggle`.
 *
 * Only the control moved. `ThemeModeProvider` still owns the preference, its persistence and
 * the `system` resolution exactly as SASBND-0008 U2 shipped them.
 *
 * The route sits under `AppLayout` but **outside** `BrandAccessGate`: appearance is a
 * property of the console, not of the tenant's brand, so a platform operator or a non-owner
 * — who the brand API rejects — must still be able to reach it.
 */
import { Box, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import type { ReactElement } from 'react';
import { useThemeMode, type ThemeMode } from '@/theme/ThemeModeProvider';
import { useCopy } from '@/hooks/useCopy';

interface ModeOption {
  mode: ThemeMode;
  labelKey: string;
  fallback: string;
  icon: ReactElement;
}

/** `system` is the default preference, and the fallback when a stored one is unrecognized. */
const OPTIONS: readonly ModeOption[] = [
  {
    mode: 'light',
    labelKey: 'appearance.light',
    fallback: 'Light',
    icon: <LightModeIcon fontSize="small" />,
  },
  {
    mode: 'dark',
    labelKey: 'appearance.dark',
    fallback: 'Dark',
    icon: <DarkModeIcon fontSize="small" />,
  },
  {
    mode: 'system',
    labelKey: 'appearance.system',
    fallback: 'Match system',
    icon: <SettingsBrightnessIcon fontSize="small" />,
  },
];

export function Settings() {
  const { t } = useCopy();
  const { mode, setMode } = useThemeMode();
  const appearance = t('appearance.label', 'Appearance');

  return (
    <Box>
      <Typography variant="h1" gutterBottom>
        {t('settings.title', 'Settings')}
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
        <Typography variant="h3" component="h2">
          {appearance}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>
          {t(
            'settings.appearanceHelp',
            'Choose how Brand Center looks on this device. Match system follows your device’s light or dark setting.',
          )}
        </Typography>
        {/* The repo's exclusive-choice control (the site-builder device switch and the
            preview zoom use the same shape). The three modes are one exclusive choice, so
            `exclusive` is what keeps `system` a visible state of its own rather than a
            deselected light/dark. */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_e, next: ThemeMode | null) => {
            // Null arrives when the selected button is clicked again — appearance always has
            // a value, so that deselection is dropped rather than written back.
            if (next) setMode(next);
          }}
          aria-label={appearance}
        >
          {OPTIONS.map((option) => (
            <ToggleButton key={option.mode} value={option.mode} sx={{ gap: 1 }}>
              {option.icon}
              {t(option.labelKey, option.fallback)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>
    </Box>
  );
}
